/**
 * pendingUploads — as fotos que um documento EM EDIÇÃO já subiu para o Storage
 * e que ainda não têm dono no banco.
 *
 * ─── POR QUE ELAS EXISTEM ───────────────────────────────────────────────────
 *
 * O upload de foto de item acontece no ATO DA SELEÇÃO, não no save, e isso é
 * deliberado: subir tudo junto no "Salvar" empilharia N uploads num momento em
 * que o usuário espera uma gravação instantânea. O preço é que o objeto passa a
 * existir no bucket antes de qualquer linha apontar para ele — e se o documento
 * nunca for salvo, ninguém mais sabe que aquele arquivo existe.
 *
 * ─── O QUE PODE E O QUE NÃO PODE SER APAGADO ────────────────────────────────
 *
 * A régua NÃO é o bucket nem a pasta: é a PROCEDÊNCIA. Só entra nesta lista o
 * que o próprio formulário subiu agora. Um item trazido do catálogo carrega a
 * URL da foto do PRODUTO, que pertence ao catálogo e sobrevive ao documento —
 * apagá-la porque o orçamento foi descartado destruiria o cadastro do produto,
 * e o estrago apareceria dias depois, em outro lugar. Por isso a lista é
 * montada no ponto do upload e viaja DENTRO do rascunho: quem não foi subido
 * aqui simplesmente não está nela.
 *
 * ─── QUANDO ELAS DEIXAM DE SER PENDENTES ────────────────────────────────────
 *
 *   documento SALVO      as que continuam referenciadas viram propriedade do
 *                        documento; as que sobraram (foto trocada, foto
 *                        removida, item inteiro excluído) são apagadas.
 *   rascunho DESCARTADO  todas são apagadas — é o único gesto que significa
 *                        "desisti deste documento".
 *
 * ⚠️ SAIR DA TELA NÃO É DESCARTAR. `/quotes/new` autossalva o rascunho e o
 * `DraftIndicator` oferece "Continuar orçamento"; apagar as fotos ao desmontar
 * a página devolveria o usuário a um rascunho cheio de URLs mortas. O gatilho é
 * o X do indicador de rascunho, não a navegação.
 */
import { supabase } from '@/integrations/supabase/client';
import { extractStorageRef } from '@/lib/mediaUrl';

/** Um objeto do Storage que este documento subiu e ainda não entregou a ninguém. */
export interface PendingUpload {
  bucket: string;
  path: string;
  /** A URL como ela foi gravada no item — é por ela que o casamento é feito. */
  url: string;
}

const isPending = (value: unknown): value is PendingUpload =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as PendingUpload).bucket === 'string' &&
  typeof (value as PendingUpload).path === 'string' &&
  typeof (value as PendingUpload).url === 'string';

/**
 * Normaliza a lista vinda do rascunho (`localStorage`), que pode ter sido
 * gravada por uma versão anterior do app ou adulterada à mão. Entrada
 * irreconhecível é DESCARTADA, nunca "consertada": a única coisa que esta lista
 * faz é apagar arquivos, e um palpite aqui apaga o arquivo errado.
 */
export function normalizePendingUploads(raw: unknown): PendingUpload[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: PendingUpload[] = [];
  for (const entry of raw) {
    if (!isPending(entry)) continue;
    const key = `${entry.bucket}/${entry.path}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ bucket: entry.bucket, path: entry.path, url: entry.url });
  }
  return out;
}

/**
 * Registra um upload recém-feito. Recebe a URL gravada no item e deriva
 * bucket/path dela — devolve a lista inalterada se a URL não for do Storage
 * (foto offline em `data:`, hotlink externo), porque aí não há o que apagar.
 */
export function trackPendingUpload(
  current: readonly PendingUpload[] | null | undefined,
  url: string,
): PendingUpload[] {
  const ref = extractStorageRef(url);
  if (!ref) return normalizePendingUploads(current);
  return normalizePendingUploads([...(current ?? []), { ...ref, url }]);
}

/**
 * Separa a lista em "ainda em uso" e "pode apagar", comparando com as URLs que
 * o documento realmente carrega agora.
 *
 * A comparação é por bucket+path, e não pela string da URL, porque a mesma
 * imagem pode ter sido gravada assinada num lugar e pública em outro.
 */
export function splitPendingUploads(
  tracked: readonly PendingUpload[] | null | undefined,
  urlsEmUso: readonly (string | null | undefined)[],
): { keep: PendingUpload[]; discard: PendingUpload[] } {
  const emUso = new Set<string>();
  for (const url of urlsEmUso) {
    const ref = extractStorageRef(url);
    if (ref) emUso.add(`${ref.bucket}/${ref.path}`);
  }

  const keep: PendingUpload[] = [];
  const discard: PendingUpload[] = [];
  for (const entry of normalizePendingUploads(tracked)) {
    (emUso.has(`${entry.bucket}/${entry.path}`) ? keep : discard).push(entry);
  }
  return { keep, discard };
}

/** Todas as URLs de foto que este formulário de orçamento carrega agora. */
export function quotePhotoUrlsInUse(data: {
  items?: readonly {
    image_url?: string | null;
    image_url_2?: string | null;
    image_url_3?: string | null;
  }[];
  generalPhotos?: readonly { imageUrl?: string | null }[];
}): string[] {
  const urls: string[] = [];
  for (const item of data.items ?? []) {
    for (const url of [item.image_url, item.image_url_2, item.image_url_3]) {
      if (typeof url === 'string' && url) urls.push(url);
    }
  }
  for (const photo of data.generalPhotos ?? []) {
    if (typeof photo?.imageUrl === 'string' && photo.imageUrl) urls.push(photo.imageUrl);
  }
  return urls;
}

/**
 * O documento foi SALVO: apaga o que sobrou sem uso e devolve a lista já
 * limpa, para o formulário parar de considerar pendentes as fotos que acabaram
 * de ganhar dono.
 *
 * ⚠️ Zerar a lista depois do save não é higiene, é SEGURANÇA: a tela de edição
 * continua montada depois de salvar, e um "descartar rascunho" posterior apaga
 * TUDO que estiver na lista — inclusive as fotos que a gravação acabou de
 * tornar oficiais.
 */
export async function releaseSavedUploads(data: {
  pendingUploads?: readonly PendingUpload[] | null;
  items?: readonly {
    image_url?: string | null;
    image_url_2?: string | null;
    image_url_3?: string | null;
  }[];
  generalPhotos?: readonly { imageUrl?: string | null }[];
} | null | undefined): Promise<number> {
  if (!data) return 0;
  const { discard } = splitPendingUploads(data.pendingUploads, quotePhotoUrlsInUse(data));
  return deletePendingUploads(discard);
}

/**
 * O rascunho foi DESCARTADO: apaga tudo que ele tinha subido.
 *
 * Recebe o JSON cru do `localStorage` porque quem descarta é o indicador de
 * rascunho, que nunca montou o formulário e só tem a string.
 */
export async function discardDraftUploads(rawDraft: string | null | undefined): Promise<number> {
  if (!rawDraft) return 0;
  try {
    const parsed = JSON.parse(rawDraft) as { pendingUploads?: unknown };
    return await deletePendingUploads(normalizePendingUploads(parsed?.pendingUploads));
  } catch (error) {
    // Rascunho ilegível: não há o que apagar com segurança. Melhor deixar o
    // arquivo órfão (a varredura acha) do que adivinhar um caminho.
    console.error('Rascunho ilegível ao descartar uploads pendentes:', error);
    return 0;
  }
}

/**
 * Apaga os objetos do Storage, agrupados por bucket.
 *
 * NUNCA lança e NUNCA avisa o usuário: isto é faxina, não uma operação que ele
 * pediu. Falhar aqui deixa um arquivo órfão — chato, recuperável por varredura
 * — enquanto um toast de erro no meio de um "descartar rascunho" faria o
 * usuário achar que o descarte não funcionou.
 */
export async function deletePendingUploads(
  uploads: readonly PendingUpload[] | null | undefined,
): Promise<number> {
  const list = normalizePendingUploads(uploads);
  if (list.length === 0) return 0;

  const porBucket = new Map<string, string[]>();
  for (const entry of list) {
    porBucket.set(entry.bucket, [...(porBucket.get(entry.bucket) ?? []), entry.path]);
  }

  let removidos = 0;
  for (const [bucket, paths] of porBucket) {
    try {
      const { error } = await supabase.storage.from(bucket).remove(paths);
      if (error) {
        console.error('Erro ao limpar uploads pendentes:', error);
        continue;
      }
      removidos += paths.length;
    } catch (error) {
      console.error('Erro ao limpar uploads pendentes:', error);
    }
  }
  return removidos;
}
